import { useDebouncedSearch, useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceFunction } from '../../../hooks/useDebounce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getMaintenanceTypes } from '../../../services/maintenanceService';
import { MaintenanceType } from '../../../types/maintenances';
import EnhancedLabeledSelect from '../../ui/EnhancedLabeledSelect';

type Props = {
  maintenanceType?: MaintenanceType;
  selectedMaintenanceTypeId?: string;
  onChangeMaintenanceTypeId?: (maintenanceTypeId?: string) => void;
  onChangeMaintenanceType?: (maintenanceType?: MaintenanceType) => void;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  maxDropdownHeight?: string;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  defaultSearch?: string;
  required?: boolean;
  isFilter?: boolean;
  inline?: boolean;
};

function MaintenanceTypeSelect({
  selectedMaintenanceTypeId,
  onChangeMaintenanceTypeId,
  onChangeMaintenanceType,
  maintenanceType,
  label = 'Tipo de mantenimiento:',
  emptyOptionText = 'Selecciona un tipo',
  showEmptyOption = false,
  maxDropdownHeight,
  helpText,
  error,
  defaultSearch,
  isFilter = false,
  required = false,
  inline,
}: Readonly<Props>) {
  const [maintenanceTypeList, setMaintenanceTypeList] = useState<MaintenanceType[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch(defaultSearch ?? '');
  const [selectedMaintenanceTypeOption, setSelectedMaintenanceTypeOption] = useState<{ id: string; label: string }>();
  const requestIdRef = useRef(0);

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadMaintenanceTypes = async () => {
      const requestId = ++requestIdRef.current;
      const { types, error, hasNext } = await getMaintenanceTypes({
        page,
        search,
      });
      if (requestId === requestIdRef.current) {
        if (!error && types && hasNext !== undefined) {
          if (page > 1 && maintenanceTypeList) {
            setMaintenanceTypeList([...maintenanceTypeList, ...types]);
            setHasNextPage(hasNext);
          } else {
            setMaintenanceTypeList(types);
            setHasNextPage(hasNext);
          }
        } else {
          setMaintenanceTypeList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadMaintenanceTypes();
  }, [page]);

  const maintenanceTypeOptions = useMemo(() => {
    if (!maintenanceTypeList) return;
    const maintenanceTypesOptions: { id: string | undefined; label: string }[] = maintenanceTypeList.map(
      (maintenanceType) => ({
        id: maintenanceType._id,
        label: maintenanceType.value,
      })
    );
    if (showEmptyOption) {
      maintenanceTypesOptions?.unshift({
        id: undefined,
        label: isFilter ? 'Todos' : emptyOptionText,
      });
    }
    if (maintenanceType) {
      if (!maintenanceTypesOptions.find((c) => c.id === maintenanceType._id))
        maintenanceTypesOptions.push({
          id: maintenanceType._id,
          label: maintenanceType.value,
        });
    }
    if (selectedMaintenanceTypeOption) {
      if (!maintenanceTypesOptions.find((c) => c.id === selectedMaintenanceTypeOption.id))
        maintenanceTypesOptions.push(selectedMaintenanceTypeOption);
    }
    return maintenanceTypesOptions;
  }, [maintenanceTypeList]);

  const onMaintenanceTypeChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeMaintenanceTypeId && onChangeMaintenanceTypeId(params.option.id as string);
      if (!!onChangeMaintenanceType) {
        const type = maintenanceTypeList?.find((type) => type._id === (params.option?.id as string));
        onChangeMaintenanceType(type);
      }
    } else {
      onChangeMaintenanceTypeId && onChangeMaintenanceTypeId(undefined);
      onChangeMaintenanceType && onChangeMaintenanceType(undefined);
    }
    setSelectedMaintenanceTypeOption(params.option as { id: string; label: string });
    setSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage(page + 1);
  };

  const handleInputChange = useDebounceFunction(function (term: string) {
    setSearch(term);
  }, 500);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={maintenanceTypeOptions}
      value={
        showEmptyOption || selectedMaintenanceTypeId !== undefined ? [{ id: selectedMaintenanceTypeId }] : undefined
      }
      onChange={onMaintenanceTypeChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      infiniteScroll
      inline={inline}
      inset={inline}
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      searchable
      maxDropdownHeight={maxDropdownHeight}
      defaultSearch={defaultSearch}
      onBlurResetsInput={false}
      caption={helpText}
      error={error}
      required={required}
    />
  );
}

export default MaintenanceTypeSelect;
